import { MenuItem } from '#/client';
import { useDataContext } from '#/hooks';
import { MenuConfig } from '#/service/config';
import { createValueCacher } from '#/service/utils';

import { getVisibleItems } from './utils/menu';

const cachedItems = createValueCacher<MenuItem[]>();

/**
 * Hook responsible for caching and filtering menu items basing on the context.
 * @param menu The menu data
 * @param cacheKey Prefix for the menuitem caching
 * @returns Menu object with cached link and main menu items
 */
export function useCachedMenu(menu: MenuConfig, cacheKey?: string): MenuConfig {
  const { linkMenuItems = [], mainMenuItems = [], ...restOfMenu } = menu;
  const { getGeneralContext } = useDataContext();

  const context = getGeneralContext() || {};

  // we need to do this filtering every time, because context changes can cause conditions to evaluate differently.
  // but if the result contains the same items as the cached one, we will use the cached one to prevent unnecessary re-renders.
  const filteredItems = cachedItems(
    `${cacheKey}-filteredItems`,
    getVisibleItems(context, mainMenuItems)
  );
  const filteredIconItems = cachedItems(
    `${cacheKey}-filteredIconItems`,
    getVisibleItems(context, linkMenuItems)
  );

  return {
    mainMenuItems: filteredItems,
    linkMenuItems: filteredIconItems,
    ...restOfMenu,
  };
}
